import React, { useState, useCallback, useRef } from 'react';
import styles from './styles.module.scss';
import { SlideInput, CapFeedback } from '../../types';
import { isValidEmail, isValidZipCode } from '../../utils/helpers';
import { nationalFromInput, nationalPhoneError, prefixError } from '../../utils/phone';
import type { OptionalConsentAnswers } from '../../../../shared/consent/OptionalConsents';

const PRIVACY_URL = 'https://www.enpal.com/it/protezione-dei-dati';

interface FormInputProps {
  inputs: SlideInput[];
  values: Record<string, string>;
  onChange: (name: string, value: string) => void;
  onSubmit: () => void;
  submitLabel?: string;
  isPhoneSlide?: boolean;
  isLocationSlide?: boolean;
  capFeedback?: CapFeedback | null;
  isCapValid?: boolean | null;
  optionalConsents?: OptionalConsentAnswers;
  onOptionalConsentsChange?: (answers: OptionalConsentAnswers) => void;
  onPhoneContinue?: (answers: OptionalConsentAnswers) => void;
}

interface PhoneConsentsProps {
  answers: OptionalConsentAnswers;
  phoneValid: boolean;
  onChange: (answers: OptionalConsentAnswers) => void;
  onContinue: (answers: OptionalConsentAnswers) => void;
}

const PhoneConsents: React.FC<PhoneConsentsProps> = ({
  answers,
  phoneValid,
  onChange,
  onContinue,
}) => {
  const partnerChecked = answers.thirdPartyMarketing === true;

  const togglePartner = () => {
    onChange({ ...answers, thirdPartyMarketing: !partnerChecked });
  };

  const handleContinue = () => {
    onContinue(answers);
  };

  return (
    <div className={styles.phoneConsents}>
      <label className={styles.partnerCard}>
        <span className={styles.partnerCardTop}>
          <span className={styles.partnerTitle}>Comunicazione dei dati ai partner</span>
          <input
            type="checkbox"
            className={`${styles.consentCheckbox} ${
              phoneValid && !partnerChecked ? styles.consentCheckboxReady : ''
            }`}
            checked={partnerChecked}
            onChange={togglePartner}
          />
        </span>
        <span className={styles.partnerDescription}>
          Acconsento a comunicare i miei dati ai partner in{' '}
          <a
            className={styles.privacyLink}
            href={PRIVACY_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
          >
            elenco
          </a>
          , per ricevere le loro offerte. Agiscono come titolari autonomi.
        </span>
      </label>

      <button
        type="button"
        className={`${styles.discoverCta} ${phoneValid ? styles.discoverCtaReady : ''}`}
        onClick={handleContinue}
      >
        Scopri la valutazione completa
      </button>

      <p className={styles.callNote}>
        Ti chiameremo per il preventivo; la chiamata può essere registrata.{' '}
        <a
          className={styles.privacyLink}
          href={PRIVACY_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Informativa privacy
        </a>
      </p>
    </div>
  );
};

/**
 * Form con validazione; slide CAP con feedback bonus.
 * Slide telefono: consenso ai partner e CTA che si attiva solo se il numero è valido e la casella è spuntata.
 */
const FormInput: React.FC<FormInputProps> = ({
  inputs,
  values,
  onChange,
  onSubmit,
  submitLabel = 'Avanti',
  isPhoneSlide = false,
  isLocationSlide = false,
  capFeedback,
  isCapValid,
  optionalConsents,
  onOptionalConsentsChange,
  onPhoneContinue,
}) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const validateInput = useCallback((input: SlideInput, value: string): string => {
    if (input.required && !value.trim()) {
      return 'Campo obbligatorio';
    }

    switch (input.name) {
      case 'phone':
        if (value) {
          const phoneMessage = nationalPhoneError(values.phonePrefix ?? '+39', value);
          if (phoneMessage && phoneMessage !== 'Campo obbligatorio') return phoneMessage;
        }
        break;
      case 'email':
        if (value && !isValidEmail(value)) {
          return 'Indirizzo email non valido';
        }
        break;
      case 'zip':
        if (value && !isValidZipCode(value)) {
          return 'CAP non valido (5 cifre)';
        }
        break;
    }

    if (input.pattern && value) {
      const regex = new RegExp(input.pattern);
      if (!regex.test(value)) {
        return 'Formato non valido';
      }
    }

    return '';
  }, [values.phonePrefix]);

  const handleChange = (input: SlideInput, value: string) => {
    const next = input.name === 'phone'
      ? nationalFromInput(value, values.phonePrefix ?? '+39')
      : value;
    onChange(input.name, next);

    if (touched[input.name]) {
      const error = validateInput(input, next);
      setErrors((prev) => ({ ...prev, [input.name]: error }));
    }
  };

  const handlePrefixChange = (value: string) => {
    const next = value.replace(/[^\d+\s]/g, '').slice(0, 6);
    onChange('phonePrefix', next);
    if (!touched.phonePrefix && !touched.phone) return;

    const nextPrefixError = touched.phonePrefix ? prefixError(next) : '';
    const phoneMessage = nationalPhoneError(next, values.phone || '');
    setErrors((prev) => ({
      ...prev,
      ...(touched.phonePrefix ? { phonePrefix: nextPrefixError } : {}),
      ...(touched.phone ? { phone: phoneMessage } : {}),
    }));
  };

  const handleBlur = (input: SlideInput) => {
    setTouched((prev) => ({ ...prev, [input.name]: true }));
    const error = validateInput(input, values[input.name] || '');
    setErrors((prev) => ({ ...prev, [input.name]: error }));
  };

  const isFormValid = useCallback(() => {
    const inputsValid = inputs.every((input) => {
      const value = values[input.name] || '';
      const error = validateInput(input, value);
      return !error;
    });

    if (isLocationSlide) {
      return inputsValid && isCapValid === true;
    }

    if (isPhoneSlide && prefixError(values.phonePrefix ?? '+39')) {
      return false;
    }

    return inputsValid;
  }, [inputs, values, validateInput, isLocationSlide, isCapValid, isPhoneSlide]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};
    let hasErrors = false;

    inputs.forEach((input) => {
      const error = validateInput(input, values[input.name] || '');
      if (error) {
        hasErrors = true;
        newErrors[input.name] = error;
      }
    });

    setErrors(newErrors);
    setTouched(
      inputs.reduce((acc, input) => ({ ...acc, [input.name]: true }), {})
    );

    if (hasErrors) {
      const firstInvalid = inputs.find((input) => newErrors[input.name]);
      if (firstInvalid) inputRefs.current[firstInvalid.name]?.focus();
    }

    if (isPhoneSlide && optionalConsents && onPhoneContinue) {
      submitPhone(optionalConsents);
      return;
    }

    if (!hasErrors && isFormValid()) {
      onSubmit();
    }
  };

  const submitPhone = (answers: OptionalConsentAnswers) => {
    const newErrors: Record<string, string> = {};
    let hasErrors = false;

    inputs.forEach((input) => {
      const error = validateInput(input, values[input.name] || '');
      if (error) {
        hasErrors = true;
        newErrors[input.name] = error;
      }
    });

    const prefixMessage = prefixError(values.phonePrefix ?? '+39');
    if (prefixMessage) {
      hasErrors = true;
      newErrors.phonePrefix = prefixMessage;
    }

    setErrors(newErrors);
    setTouched(
      inputs.reduce(
        (acc, input) => ({ ...acc, [input.name]: true }),
        { phonePrefix: true } as Record<string, boolean>
      )
    );

    if (hasErrors || !isFormValid() || !onPhoneContinue) {
      if (prefixMessage) {
        inputRefs.current.phonePrefix?.focus();
      } else {
        const firstInvalid = inputs.find((input) => newErrors[input.name]);
        if (firstInvalid) inputRefs.current[firstInvalid.name]?.focus();
      }
      return;
    }

    const submitted: OptionalConsentAnswers = {
      enpalMarketingConsent: false,
      thirdPartyMarketing: answers.thirdPartyMarketing === true,
      thirdPartyProfiling: false,
    };
    onOptionalConsentsChange?.(submitted);
    onPhoneContinue(submitted);
  };

  const renderCapFeedback = () => {
    if (!isLocationSlide) return null;

    const zipValue = values['zip'] || '';

    if (zipValue.length < 5) {
      return null;
    }

    if (isCapValid === false) {
      return (
        <div className={`${styles.capFeedbackMessage} ${styles.invalid}`}>
          CAP non valido
        </div>
      );
    }

    if (isCapValid === true && capFeedback) {
      return (
        <div className={`${styles.capFeedbackMessage} ${styles.valid}`}>
          <span className={styles.bonusCount}>{capFeedback.bonusCount} bonus</span> disponibili per il CAP{' '}
          <strong>{zipValue}</strong>
        </div>
      );
    }

    return null;
  };

  const getAutocomplete = (name: string): string => {
    switch (name) {
      case 'name':
        return 'given-name';
      case 'email':
        return 'email';
      case 'phone':
        return 'tel';
      case 'zip':
        return 'postal-code';
      default:
        return 'off';
    }
  };

  const getAriaLabel = (name: string): string | undefined => {
    if (name === 'name') return 'Nome';
    if (name === 'email') return 'Email';
    if (name === 'phone') return 'Numero di telefono';
    return undefined;
  };

  const getInputMode = (input: SlideInput): React.HTMLAttributes<HTMLInputElement>['inputMode'] => {
    if (input.name === 'email') return 'email';
    if (input.name === 'phone' || input.name === 'zip') return 'tel';
    return undefined;
  };

  return (
    <form className={styles.formGroup} onSubmit={handleSubmit}>
      {isLocationSlide && (
        <div className={styles.capFeedbackContainer}>
          {renderCapFeedback()}
        </div>
      )}

      {inputs.map((input) => {
        if (isPhoneSlide && input.name === 'phone') {
          const prefix = values.phonePrefix ?? '+39';
          const prefixInvalid = Boolean(touched.phonePrefix && errors.phonePrefix);
          const phoneInvalid = Boolean(touched.phone && errors.phone);

          return (
            <div key={input.name} className={styles.inputWrapper}>
              <div className={styles.phoneRow}>
                <input
                  ref={(node) => {
                    inputRefs.current.phonePrefix = node;
                  }}
                  id="field-phonePrefix"
                  className={`${styles.input} ${styles.prefixInput} ${
                    prefixInvalid ? styles.inputError : ''
                  }`}
                  type="tel"
                  name="phonePrefix"
                  inputMode="tel"
                  aria-label="Prefisso"
                  autoComplete="tel-country-code"
                  value={prefix}
                  onChange={(event) => handlePrefixChange(event.target.value)}
                  onBlur={() => {
                    setTouched((prev) => ({ ...prev, phonePrefix: true }));
                    setErrors((prev) => ({ ...prev, phonePrefix: prefixError(prefix) }));
                  }}
                  aria-invalid={prefixInvalid}
                  aria-describedby={prefixInvalid ? 'error-phonePrefix' : undefined}
                />
                <input
                  ref={(node) => {
                    inputRefs.current.phone = node;
                  }}
                  id="field-phone"
                  className={`${styles.input} ${styles.phoneInput} ${styles.phoneNumberInput} ${
                    phoneInvalid ? styles.inputError : ''
                  }`}
                  type="tel"
                  name="phone"
                  inputMode="tel"
                  aria-label="Numero di telefono"
                  autoComplete="tel-national"
                  placeholder={input.placeholder}
                  value={values.phone || ''}
                  onChange={(event) => handleChange(input, event.target.value)}
                  onBlur={() => handleBlur(input)}
                  required={input.required}
                  aria-invalid={phoneInvalid}
                  aria-describedby={phoneInvalid ? 'error-phone' : undefined}
                />
              </div>
              {prefixInvalid && (
                <p id="error-phonePrefix" className={styles.errorMessage}>
                  {errors.phonePrefix}
                </p>
              )}
              {phoneInvalid && (
                <p id="error-phone" className={styles.errorMessage}>
                  {errors.phone}
                </p>
              )}
            </div>
          );
        }

        return (
          <div key={input.name} className={styles.inputWrapper}>
            <div className={isLocationSlide && input.name === 'zip' ? styles.inputWithCheck : ''}>
              <input
                ref={(node) => {
                  inputRefs.current[input.name] = node;
                }}
                id={`field-${input.name}`}
                className={`${styles.input} ${
                  touched[input.name] && errors[input.name] ? styles.inputError : ''
                }`}
                type={input.type}
                name={input.name}
                inputMode={getInputMode(input)}
                placeholder={input.placeholder}
                value={values[input.name] || ''}
                onChange={(e) => handleChange(input, e.target.value)}
                onBlur={() => handleBlur(input)}
                required={input.required}
                maxLength={input.maxLength}
                autoComplete={getAutocomplete(input.name)}
                aria-label={getAriaLabel(input.name)}
                aria-invalid={touched[input.name] && Boolean(errors[input.name])}
                aria-describedby={errors[input.name] ? `error-${input.name}` : undefined}
              />
              {isLocationSlide && input.name === 'zip' && values['zip']?.length === 5 && (
                <span
                  className={`${styles.checkIcon} ${
                    isCapValid === true ? styles.valid : isCapValid === false ? styles.invalid : ''
                  }`}
                >
                  {isCapValid === true ? '✓' : isCapValid === false ? '✗' : ''}
                </span>
              )}
            </div>
            {touched[input.name] && errors[input.name] && (
              <p id={`error-${input.name}`} className={styles.errorMessage}>
                {errors[input.name]}
              </p>
            )}
          </div>
        );
      })}

      {!isPhoneSlide && (
        <div className={styles.buttonGroup}>
          <button type="submit" className={styles.btnNext} disabled={!isFormValid()}>
            {submitLabel}
          </button>
        </div>
      )}

      {isPhoneSlide && optionalConsents && onOptionalConsentsChange && onPhoneContinue && (
        <PhoneConsents
          answers={optionalConsents}
          phoneValid={isFormValid()}
          onChange={onOptionalConsentsChange}
          onContinue={submitPhone}
        />
      )}
    </form>
  );
};

export default FormInput;
